import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './utils/apiClient'; // register axios auth interceptor
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RBACProvider } from './contexts/RBACContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardLayout from './layouts/DashboardLayout';
import DashboardHome from './pages/DashboardHome';
import PlacementsPage from './pages/PlacementsPage';
import NewPlacementPage from './pages/NewPlacementPage';
import PlacementDetailPage from './pages/PlacementDetailPage';
import EarningsPage from './pages/EarningsPage';
import RoleIncentivePage from './pages/RoleIncentivePage';
import ReviewQueuePage from './pages/ReviewQueuePage';
import TeamOverviewPage from './pages/TeamOverviewPage';

// Admin Pages
import UserManagementPage from './pages/admin/UserManagementPage';
import TeamManagementPage from './pages/admin/TeamManagementPage';
import AccessControlPage from './pages/admin/AccessControlPage';
import CeoDashboardPage from './pages/CeoDashboardPage';
import VendorMatrixPage from './pages/admin/VendorMatrixPage';
import IncentiveMatrixPage from './pages/admin/IncentiveMatrixPage';
import OrganizationsPage from './pages/admin/OrganizationsPage';
import SplitConfigPage from './pages/admin/SplitConfigPage';
import BonusRulesPage from './pages/admin/BonusRulesPage';
import VendorClaimsAdminPage from './pages/admin/VendorClaimsAdminPage';
import DiscountManagementPage from './pages/admin/DiscountManagementPage';
import PayrollExportPage from './pages/admin/PayrollExportPage';
import EntityManagementPage from './pages/admin/EntityManagementPage';
import TargetsPage from './pages/admin/TargetsPage';
import BonusTrackerPage from './pages/admin/BonusTrackerPage';
import SupportedPlacementsPage from './pages/SupportedPlacementsPage';
import BackoutsPage from './pages/BackoutsPage';
import ReportingStructurePage from './pages/ReportingStructurePage';
import OrganizationChartPage from './pages/OrganizationChartPage';
import CustomFieldsPage from './pages/admin/CustomFieldsPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import UserProfilePage from './pages/UserProfilePage';
import ManagerDetailPage from './pages/ManagerDetailPage';
import OrgDetailPage from './pages/OrgDetailPage';
import ReportsPage from './pages/ReportsPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null, deniedRoles = null }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (deniedRoles && deniedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route
          path="ceo"
          element={
            <ProtectedRoute allowedRoles={['ceo', 'superadmin']}>
              <CeoDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route path="placements" element={<PlacementsPage />} />
        <Route path="placements/new" element={<NewPlacementPage />} />
        <Route path="placements/:id" element={<PlacementDetailPage />} />
        <Route path="placements/:id/edit" element={<NewPlacementPage />} />
        <Route path="earnings" element={<EarningsPage />} />
        <Route
          path="role-incentives"
          element={
            <ProtectedRoute allowedRoles={[
              'sales_lead', 'recruiting_lead',
              'sales_manager', 'recruiting_manager',
              'sales_assistant_manager', 'recruiting_assistant_manager',
            ]}>
              <RoleIncentivePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="review-queue"
          element={
            <ProtectedRoute allowedRoles={['sales_recruiter', 'technical_recruiter', 'sales_technical_recruiter', 'sales_lead', 'recruiting_lead', 'sales_manager', 'recruiting_manager', 'onsite_manager', 'superadmin', 'ceo']}>
              <ReviewQueuePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="team"
          element={
            <ProtectedRoute allowedRoles={['sales_lead', 'recruiting_lead', 'sales_manager', 'recruiting_manager', 'onsite_manager', 'superadmin', 'ceo']}>
              <TeamOverviewPage />
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="admin/users"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo', 'people_admin']}>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/teams"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo', 'people_admin']}>
              <TeamManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/vendor-matrix"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <VendorMatrixPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/incentive-matrix"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <IncentiveMatrixPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/organizations"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <OrganizationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/split-config"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <SplitConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/bonus-rules"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <BonusRulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/vendor-claims"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <VendorClaimsAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/access-control"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <AccessControlPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/payroll"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <PayrollExportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/discounts"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <DiscountManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/entities"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <EntityManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/targets"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <TargetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/bonus-tracker"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'ceo']}>
              <BonusTrackerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="supported-placements"
          element={
            <ProtectedRoute deniedRoles={['people_admin']}>
              <SupportedPlacementsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="backouts"
          element={
            <ProtectedRoute deniedRoles={['people_admin']}>
              <BackoutsPage />
            </ProtectedRoute>
          }
        />
        <Route path="reporting-structure" element={<ReportingStructurePage />} />
        <Route path="organization-chart" element={<OrganizationChartPage />} />
        <Route path="user/:userId" element={<UserProfilePage />} />
        <Route path="manager/:managerId" element={<ManagerDetailPage />} />
        <Route path="org/:orgName" element={<OrgDetailPage />} />
              <Route path="reports" element={<ReportsPage />} />
        <Route path="admin/custom-fields" element={<ProtectedRoute allowedRoles={['superadmin', 'ceo']}><CustomFieldsPage /></ProtectedRoute>} />
        <Route path="admin/audit-log" element={<ProtectedRoute allowedRoles={['superadmin', 'ceo']}><AuditLogPage /></ProtectedRoute>} />
      </Route>

      {/* Redirect root to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <RBACProvider>
            <AppRoutes />
            <Toaster position="top-right" />
          </RBACProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
